import { useQuery, UseQueryOptions } from '@tanstack/react-query'; //-tech-debt-react-query;
import { useSelector } from 'react-redux';
import { fetchItem, fetchItems } from '../support/queries';
import { CacheKey } from '../types/cache-keys';
import { RootState } from '../support/store';

// ---- Fetches ----

const useCurrentUserEmail = () => {
  return useSelector((state: RootState) => state.user.userData.email);
};

const useHasToolboxToken = () => {
  return useSelector((state: RootState) => Boolean(state.user.toolboxToken));
};

const useFetchSfCustomer = (options?) => {
  const email = useCurrentUserEmail();
  const hasToolboxToken = useHasToolboxToken();

  return useQuery({
    queryKey: [CacheKey.SalesforceCustomers, email],
    queryFn: () => fetchItem(`sf/customer`, email ? { email } : {}),
    ...{
        enabled: hasToolboxToken && ((options?.enabled ?? true) as boolean),
        ...options,
      },
  });
};

const useFetchSfCustomerById = (
  customerId: any,
  options?: Omit<UseQueryOptions<any, unknown, any, string[]>, 'queryKey' | 'queryFn'>
) => {
  const email = useCurrentUserEmail();
  const hasToolboxToken = useHasToolboxToken();

  return useQuery({
    queryKey: [CacheKey.SalesforceCustomer, customerId, email],
    queryFn: () => fetchItem(`sf/customer/${customerId}`, email ? { email } : {}),
    ...{
        enabled: !!customerId && hasToolboxToken && ((options?.enabled ?? true) as boolean),
        ...options,
      },
  });
};

const useFetchSfOpportunity = (options?) => {
  const email = useCurrentUserEmail();
  const hasToolboxToken = useHasToolboxToken();

  return useQuery({
    queryKey: [CacheKey.SalesforceOpportunities, email],
    queryFn: () => fetchItems(`sf/opportunity`, email ? { email, size: 2000 } : { size: 2000 }),
    ...{
        enabled: hasToolboxToken && ((options?.enabled ?? true) as boolean),
        ...options,
      },
  });
};

type SalesforceQueryData<TRecord> = TRecord[] | { records: TRecord[] };

const useFetchSfQuery = <TRecord = Record<string, unknown>>(
  queryStr: string,
  options?: Omit<UseQueryOptions<SalesforceQueryData<TRecord>>, 'queryKey' | 'queryFn'>
) => {
  const hasToolboxToken = useHasToolboxToken();

  return useQuery<SalesforceQueryData<TRecord>>({
    queryKey: [CacheKey.SalesforceQuery, queryStr],
    queryFn: () => fetchItems('sf/query', { q: queryStr }),
    ...{
        enabled: !!queryStr && hasToolboxToken && ((options?.enabled ?? true) as boolean),
        ...options,
      },
  });
};

// ---- Export ----

export {
  useFetchSfCustomer,
  useFetchSfCustomerById,
  useFetchSfOpportunity,
  useFetchSfQuery,
};